import { useWorkspace } from '@/contexts/WorkspaceContext'
import { cn } from '@/lib/cn'
import { getLucideIcon } from '@/lib/lucideIcon'
import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function WorkspaceSwitcher() {
  const { t } = useTranslation('workspaces')
  const { current, available, switchTo } = useWorkspace()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!current) return null

  const CurrentIcon = getLucideIcon(current.icon)
  const currentName = t(`workspaces.${current.code}.name`, current.name)

  if (available.length <= 1) {
    return (
      <span className="hidden items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-foreground sm:inline-flex">
        <CurrentIcon className="h-4 w-4 text-muted-foreground" />
        {currentName}
      </span>
    )
  }

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium',
          'text-foreground transition-colors hover:bg-muted focus:outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <CurrentIcon className="h-4 w-4 text-muted-foreground" />
        <span className="hidden max-w-[160px] truncate sm:inline">{currentName}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute left-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-xl',
            'border border-border bg-background shadow-lg',
          )}
        >
          <p className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('switcher.change')}
          </p>
          <div className="max-h-[60vh] overflow-y-auto p-1.5">
            {available.map((ws) => {
              const Icon = getLucideIcon(ws.icon)
              const isSelected = ws.code === current.code
              const name = t(`workspaces.${ws.code}.name`, ws.name)
              const description = t(
                `workspaces.${ws.code}.description`,
                ws.description ?? '',
              )
              return (
                <button
                  type="button"
                  key={ws.code}
                  role="menuitem"
                  onClick={() => {
                    setOpen(false)
                    if (!isSelected) switchTo(ws.code)
                  }}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors',
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted',
                  )}
                >
                  <Icon
                    className={cn(
                      'mt-0.5 h-4 w-4 flex-shrink-0',
                      isSelected
                        ? 'text-primary-foreground'
                        : 'text-muted-foreground',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight">{name}</p>
                    {description ? (
                      <p
                        className={cn(
                          'mt-0.5 truncate text-xs',
                          isSelected
                            ? 'text-foreground'
                            : 'text-muted-foreground',
                        )}
                      >
                        {description}
                      </p>
                    ) : null}
                  </div>
                  {isSelected ? (
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
